import {CommonModule} from '@angular/common';
import {Component, inject, input, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CustomEditorComponent} from '@components-common/custom-editor/custom-editor.component';
import {UserExternProps} from '@core/models/customer-extern';
import {AuthService} from '@core/services/auth.service';
import {DemandaService} from '@core/services/demanda.service';
import {TipoRepresentacaoService} from '@core/services/tipo-representacao.service';
import {UsuarioExternoService} from '@core/services/usuario-externo.service';
import {ID_TIPO_REPRESENTACAO_GOVBR_KEY} from '@shared/constants/storage-keys';
import {Checkbox} from 'primeng/checkbox';
import {Fluid} from 'primeng/fluid';
import {InputMask} from 'primeng/inputmask';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';

@Component({
  selector: 'app-form-descricao-demanda',
  imports: [
    CommonModule, ReactiveFormsModule,
    Select, InputText, Checkbox, InputMask, Fluid, CustomEditorComponent,
  ],
  templateUrl: './form-descricao-demanda.component.html',
  styleUrl: './form-descricao-demanda.component.css',
})
export class FormDescricaoDemandaComponent implements OnInit {
  private static nextId = 0;
  protected readonly fieldId = `form-descricao-${++FormDescricaoDemandaComponent.nextId}`;
  private readonly tipoRepresentacaoService = inject(TipoRepresentacaoService);
  private readonly demandaService = inject(DemandaService);
  protected tipoRepresentacaoOptions: { label: string; value: { id: number; nome: string } }[] = [];
  readonly form = input.required<FormGroup>();
  readonly editMode = input<boolean>(true);
  readonly hideTipoRepresentacaoFormField = input<boolean>(false);
  protected tipoProcessoOptions: { label: string; value: { id: number; nome: string } }[] = [];
  private readonly authService = inject(AuthService);
  private readonly usuarioExternoService = inject(UsuarioExternoService);
  protected readonly ID_TIPO_PROCESSO_SEI_OUTROS = 7;
  protected isTipoRepresentacaoConcessionaria = false;

  ngOnInit(): void {
    if (this.hideTipoRepresentacaoFormField()) {
      const tipoRepresentacaoControl = this.form().get('tipoRepresentacao');
      tipoRepresentacaoControl?.clearValidators();
      tipoRepresentacaoControl?.updateValueAndValidity();
    } else {
      this.tipoRepresentacaoService.getEnums().subscribe((data) => {
        this.tipoRepresentacaoOptions = data.map((item) => ({
          label: item.nome,
          value: {id: item.id, nome: item.nome}
        }));

        if (this.editMode()) {
          if (this.authService.isAuthenticatedGovBr()) {
            const idStorage = sessionStorage.getItem(ID_TIPO_REPRESENTACAO_GOVBR_KEY);
            if (idStorage) {
              const idTipoRepresentacao = JSON.parse(idStorage) as number;
              const option = this.tipoRepresentacaoOptions.find(opt => opt.value.id === idTipoRepresentacao);
              if (option) {
                this.form().get('tipoRepresentacao')?.setValue(option.value);
                this.form().get('tipoRepresentacao')?.disable();
              }
            }
          } else if (this.authService.isAuthenticatedConcessionaria()) {
            this.form().get('tipoRepresentacao')?.clearValidators();
            this.form().get('tipoRepresentacao')?.updateValueAndValidity();
            this.isTipoRepresentacaoConcessionaria = true;
            this.applyTipoRepresentacaoConcessionaria();
          }
        } else if (this.form().get('representacaoUsuarioConcessionaria')?.value) {
          this.isTipoRepresentacaoConcessionaria = true;
        }
      });
    }

    this.demandaService.listarTiposProcesso().subscribe((data) => {
      this.tipoProcessoOptions = data.map((item) => ({
        label: item.nome,
        value: {id: item.id, nome: item.nome}
      }));
    });

    this.form().get('existeProcessoSei')!.valueChanges.subscribe(value => {
      const numeroControl = this.form().get('numeroProcessoSei')!;
      const tipoControl = this.form().get('tipoProcessoSei')!;
      const descricaoProcessoControl = this.form().get('descricaoProcesso')!;

      if (value) {
        numeroControl.setValidators(Validators.required);
        tipoControl.setValidators(Validators.required);
        numeroControl.updateValueAndValidity();
        tipoControl.updateValueAndValidity();
      } else {
        numeroControl.clearValidators();
        tipoControl.clearValidators();
        descricaoProcessoControl?.clearValidators();
        numeroControl.updateValueAndValidity();
        tipoControl.updateValueAndValidity({emitEvent: false});
        descricaoProcessoControl?.updateValueAndValidity();
      }
    });

    this.form().get('tipoProcessoSei')!.valueChanges.subscribe(value => {
      const descricaoProcessoControl = this.form().get('descricaoProcesso');
      if (!value) {
        return;
      }
      if (value.id === this.ID_TIPO_PROCESSO_SEI_OUTROS) {
        descricaoProcessoControl?.setValidators(Validators.required);
      } else {
        descricaoProcessoControl?.clearValidators();
      }
      descricaoProcessoControl?.updateValueAndValidity();
    })
  }

  protected isFormControlInvalid(controlName: string): boolean {
    const control = this.form().get(controlName);
    return !!(control?.touched && control?.invalid);
  }

  protected asFormControl(controlName: string): FormControl {
    return this.form().get(controlName) as FormControl;
  }

  protected getNomeFromEnum(formField: string): string {
    const value = this.form().get(formField)?.value;
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.nome ?? '';
  }

  private applyTipoRepresentacaoConcessionaria(): void {
    const usuario = this.usuarioExternoService.usuario;
    if (usuario) {
      this.setTipoRepresentacaoConcessionaria(usuario);
      return;
    }

    this.usuarioExternoService.getUsuarioLogado().subscribe({
      next: (user) => this.setTipoRepresentacaoConcessionaria(user),
    });
  }

  private setTipoRepresentacaoConcessionaria(usuario: UserExternProps): void {
    const value = usuario.concessionariaNome?.trim() || usuario.outro?.trim() || '';
    const control = this.form().get('representacaoUsuarioConcessionaria');
    control?.setValue(value);
    control?.disable();
  }

}
